/**
 * Ownership — what the player has, and how they got it.
 *
 * ── Why this is an interface with a local implementation ────────────────────
 *
 * `LocalEntitlementStore` is a DEVELOPMENT implementation. It keeps ownership
 * in localStorage, which is correct for coin purchases (a soft currency earned
 * on this device) and NOT correct for anything bought with money.
 *
 * The difference matters enormously:
 *
 *   - Coins are earned locally. If a player edits them, they have cheated
 *     themselves out of a grind in a single-player football game. Nobody is
 *     harmed and nothing is owed.
 *   - Cash entitlements are a CONTRACT. A player who pays and then clears their
 *     browser data has lost something they are owed, and no amount of client
 *     code can restore it — only a server that recorded the receipt can.
 *
 * So cash purchases refuse to complete unless a `PaymentProvider` is installed,
 * and there is deliberately no default one. A store with a live BUY button and
 * no payment path is worse than one that says "coming soon", because the first
 * takes money it cannot honour and the second is merely unfinished.
 *
 * When the backend lands: implement `EntitlementStore` against it, swap the
 * instance in `entitlements()`, and no screen changes. That is the entire point
 * of the seam, and it is why it exists before the backend rather than after —
 * retrofitting it means migrating live purchase data.
 */

import type {
  CatalogueItem, Entitlement, EntitlementStore, PaymentProvider, PurchaseResult,
} from './types.js';
import { credit, debit, loadWallet } from './wallet.js';

export const ENTITLEMENTS_KEY = 'fg.entitlements.v1';

interface Persisted {
  version: 1;
  owned: Entitlement[];
}

function read(): Persisted {
  try {
    const raw = localStorage.getItem(ENTITLEMENTS_KEY);
    if (!raw) return { version: 1, owned: [] };
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    return { version: 1, owned: Array.isArray(parsed.owned) ? parsed.owned : [] };
  } catch {
    // Never brick the store on corrupt data — an empty cabinet is recoverable,
    // an exception on menu mount is not.
    return { version: 1, owned: [] };
  }
}

function write(p: Persisted): void {
  try {
    localStorage.setItem(ENTITLEMENTS_KEY, JSON.stringify(p));
  } catch {
    /* quota / private mode */
  }
}

/** Installed by the app when a real provider exists. Null means cash is off. */
let provider: PaymentProvider | null = null;

export function installPaymentProvider(p: PaymentProvider | null): void {
  provider = p;
}

export function paymentAvailable(): boolean {
  return provider !== null;
}

class LocalEntitlementStore implements EntitlementStore {
  list(): readonly Entitlement[] {
    return read().owned;
  }

  owns(itemId: string): boolean {
    return read().owned.some((e) => e.itemId === itemId);
  }

  buyWithCoins(item: CatalogueItem): PurchaseResult {
    if (item.coinPrice === null) return { ok: false, reason: 'not-purchasable' };
    // Coin packs are bought WITH money, never with the currency they grant.
    if (item.kind === 'coins') return { ok: false, reason: 'not-purchasable' };
    if (this.owns(item.id)) return { ok: false, reason: 'already-owned' };

    const wallet = loadWallet();
    if (wallet.balance < item.coinPrice) return { ok: false, reason: 'insufficient-coins' };

    /*
     * Debit FIRST, grant second.
     *
     * If the grant somehow fails after a successful debit the player is short
     * coins but can be made whole; the other order can mint an item for free
     * and, worse, do it repeatedly. When in doubt, fail in the direction that
     * is recoverable.
     */
    const key = `buy:${item.id}:${Date.now()}`;
    if (!debit(key, item.coinPrice, `Bought ${item.name}`)) {
      return { ok: false, reason: 'insufficient-coins' };
    }

    const entitlement: Entitlement = {
      itemId: item.id,
      source: 'coins',
      at: Date.now(),
      receipt: null,
    };
    const p = read();
    p.owned.push(entitlement);
    write(p);
    return { ok: true, entitlement, balance: loadWallet().balance };
  }

  async buyWithCash(item: CatalogueItem): Promise<PurchaseResult> {
    if (item.cashPriceMinor === null) return { ok: false, reason: 'not-purchasable' };
    if (item.kind !== 'coins' && this.owns(item.id)) return { ok: false, reason: 'already-owned' };
    // No provider means no honest way to take money. Say so.
    if (!provider) return { ok: false, reason: 'unavailable' };

    let receipt: string | null = null;
    try {
      receipt = await provider.charge(item);
    } catch {
      receipt = null;
    }
    if (!receipt) return { ok: false, reason: 'payment-failed' };

    return this.grantFromReceipt(item, receipt);
  }

  /**
   * Turn a verified receipt into what the player bought. Shared by purchase and
   * restore, so a re-grant behaves identically to the original sale.
   */
  private grantFromReceipt(item: CatalogueItem, receipt: string): PurchaseResult {
    if (item.kind === 'coins' && item.grantsCoins) {
      // Keyed on the RECEIPT, so replaying a restore cannot mint coins twice.
      credit(`receipt:${receipt}`, item.grantsCoins, 'purchase', `Bought ${item.name}`);
      const entitlement: Entitlement = {
        itemId: item.id, source: 'cash', at: Date.now(), receipt,
      };
      return { ok: true, entitlement, balance: loadWallet().balance };
    }

    const entitlement: Entitlement = {
      itemId: item.id, source: 'cash', at: Date.now(), receipt,
    };
    const p = read();
    if (!p.owned.some((e) => e.itemId === item.id)) {
      p.owned.push(entitlement);
      write(p);
    }
    return { ok: true, entitlement, balance: loadWallet().balance };
  }

  async restore(): Promise<number> {
    if (!provider) return 0;
    const { itemById } = await import('./catalogue.js');
    let restored = 0;
    for (const r of await provider.receipts()) {
      const item = itemById(r.itemId);
      if (!item) continue;
      if (item.kind !== 'coins' && this.owns(item.id)) continue;
      const res = this.grantFromReceipt(item, r.receipt);
      if (res.ok) restored++;
    }
    return restored;
  }
}

const store: EntitlementStore = new LocalEntitlementStore();

export function entitlements(): EntitlementStore {
  return store;
}

/** Grant something without payment — rewards, promos, compensation. */
export function grantItem(itemId: string, source: Entitlement['source'] = 'reward'): void {
  const p = read();
  if (p.owned.some((e) => e.itemId === itemId)) return;
  p.owned.push({ itemId, source, at: Date.now(), receipt: null });
  write(p);
}

/** The celebration the player has equipped, or null. */
export const EQUIPPED_KEY = 'fg.equipped.v1';

export function equippedCelebration(): string | null {
  try {
    return localStorage.getItem(EQUIPPED_KEY);
  } catch {
    return null;
  }
}

export function equipCelebration(itemId: string | null): void {
  try {
    if (itemId === null) localStorage.removeItem(EQUIPPED_KEY);
    else localStorage.setItem(EQUIPPED_KEY, itemId);
  } catch {
    /* private mode */
  }
}
